// The interaction prompt (gate C5, decision 0044): over the thing the chooser picked, a key cap and a verb, "[F] Read",
// in the world (projected from the thing's place each frame). The cap is the `interact` action's glyph for the device
// used last (the input's glyphs: a key, a pad button, or a tap on touch), the verb comes through the strings table.
// On touch, the overlay's interact button shows while something is in reach. Also the sign's text while one is read
// and a small strip with the bag's counts. DOM only; the HUD package (phase D2) takes this over.
// Since the composition framework's flow stream the prompt itself (the cap, the verb, its place, the touch button) is
// the interact-prompt feature's (@rpg-unit/interact-prompt); the sign's text and the bag strip stay the things'.
import * as THREE from "three";
import { createInteractPrompt } from "@rpg-unit/interact-prompt";
import type { DeviceFamily, Input } from "@rpg/runtime";
import type { TouchOverlay } from "@rpg/runtime/dom";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { Things } from "./module";

const CSS = `
.gv-sign{position:fixed;left:50%;top:18%;transform:translateX(-50%);z-index:12;display:none;max-width:min(80vw,520px);padding:12px 16px;border-radius:10px;
  background:#efe1bf;color:#3b2a18;font:500 16px/1.4 Georgia,serif;box-shadow:0 4px 18px rgba(0,0,0,.25);pointer-events:none}
.gv-sign[data-shown]{display:block}
.gv-bag{position:fixed;right:14px;top:12px;z-index:11;display:none;gap:10px;padding:5px 10px;border-radius:9px;background:rgba(20,28,24,.55);color:#f4efe2;
  font:500 13px/1.2 system-ui,sans-serif;pointer-events:none}
.gv-bag[data-shown]{display:flex}
.gv-bag b{font-weight:700}
`;

/** What the prompt shows (for the handle and the specs). */
export interface InteractPromptState {
  shown: boolean;
  id: string | null;
  device: DeviceFamily | null;
  family: DeviceFamily | null;
  cap: string;
  verb: string;
  /** Screen position (px) of the prompt's anchor. */
  x: number;
  y: number;
  touchButton: boolean;
  sign: string | null;
  bag: string[];
}

/** Mount the prompt, the sign panel and the bag strip. */
export function mountInteractPrompt(o: {
  runtime: Runtime;
  input: Input;
  overlay: TouchOverlay;
  glyphs: Readonly<Partial<Record<DeviceFamily, Readonly<Record<string, string>>>>>;
  content: SpikeContent;
  camera: THREE.Camera;
  things: () => Things;
  /** In play (the flow's play state, no screen over it). */
  playing: () => boolean;
}): { state(): InteractPromptState } {
  const t = o.content.t;
  const v = new THREE.Vector3();
  // the prompt (@rpg-unit/interact-prompt): over what the chooser picked while in play and not busy, placed above the
  // thing's feet through the camera each frame
  const prompt = createInteractPrompt({
    input: o.input,
    glyphs: o.glyphs,
    t,
    choice: () => {
      const th = o.things();
      const ch = th.choice();
      return { id: o.playing() && !th.state().acting ? ch.id : null, verb: ch.verb };
    },
    screenAt: (id) => {
      const p = o.things().at(id);
      v.set(p[0], p[1] + 1.55, p[2]).project(o.camera);
      return { x: (v.x * 0.5 + 0.5) * innerWidth, y: (-v.y * 0.5 + 0.5) * innerHeight };
    },
    overlay: o.overlay,
  });
  if (!document.getElementById("gv-interact-css")) {
    const s = document.createElement("style");
    s.id = "gv-interact-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const sign = document.createElement("div");
  sign.className = "gv-sign";
  const bag = document.createElement("div");
  bag.className = "gv-bag";
  document.body.append(sign, bag);
  let st: InteractPromptState = { shown: false, id: null, device: null, family: null, cap: "", verb: "", x: 0, y: 0, touchButton: false, sign: null, bag: [] };

  o.runtime.on(
    "late",
    () => {
      prompt.update();
      const th = o.things();
      const s = th.state();
      const playing = o.playing();
      // the sign's text
      const reading = playing && s.reading ? th.content.interactables.get(th.placement(s.reading.id).piece)?.text?.id ?? null : null;
      if (reading) {
        const text = t(reading);
        if (sign.textContent !== text) sign.textContent = text;
        sign.dataset.shown = "";
      } else sign.removeAttribute("data-shown");
      // the bag: each item's name and count
      const lines = s.bag.stacks.length ? [...new Set(s.bag.stacks.map((x) => x.item))].map((item) => t("things.count", { item: t(th.content.items.get(item).name), count: s.bag.stacks.filter((x) => x.item === item).reduce((n, x) => n + x.count, 0) })) : [];
      const key = lines.join("|");
      if (bag.dataset.key !== key) {
        bag.dataset.key = key;
        const title = document.createElement("b");
        title.textContent = t("things.bag");
        bag.replaceChildren(
          title,
          ...lines.map((l) => {
            const span = document.createElement("span");
            span.textContent = l;
            return span;
          }),
        );
      }
      if (playing && lines.length) bag.dataset.shown = "";
      else bag.removeAttribute("data-shown");
      st = { ...prompt.state(), sign: reading ? t(reading) : null, bag: lines };
    },
    { order: 50, name: "things: prompt" },
  );
  return { state: () => ({ ...st, bag: [...st.bag] }) };
}
